/**
 * API Error codes for consistent error handling across the application
 */
export enum ApiErrorCode {
  LABEL_CONTACT_LABELS_FETCH_FAILED = 'LABEL_CONTACT_LABELS_FETCH_FAILED',
  LABEL_CONTACTS_BY_LABEL_FETCH_FAILED = 'LABEL_CONTACTS_BY_LABEL_FETCH_FAILED',
  LABEL_SEARCH_BY_LABEL_FAILED = 'LABEL_SEARCH_BY_LABEL_FAILED',
  AUTH_USER_NOT_FOUND = 'auth/user-not-found',
  AUTH_WRONG_PASSWORD = 'auth/wrong-password',
  AUTH_OPERATION_NOT_ALLOWED = 'auth/operation-not-allowed',
  AUTH_INVALID_ACTION_CODE = 'auth/invalid-action-code',
  AUTH_EXPIRED_ACTION_CODE = 'auth/expired-action-code',
  AUTH_INVALID_VERIFICATION_CODE = 'auth/invalid-verification-code',
  AUTH_INVALID_VERIFICATION_ID = 'auth/invalid-verification-id',
  AUTH_MISSING_EMAIL = 'auth/missing-email',
  AUTH_MISSING_PASSWORD = 'auth/missing-password',
  AUTH_MISSING_CODE = 'auth/missing-code',
  AUTH_MISSING_CONTINUE_URI = 'auth/missing-continue-uri',
  AUTH_INVALID_CONTINUE_URI = 'auth/invalid-continue-uri',
  AUTH_INVALID_ID_TOKEN = 'auth/invalid-id-token',
  AUTH_USER_TOKEN_EXPIRED = 'auth/user-token-expired',
  AUTH_INVALID_REFRESH_TOKEN = 'auth/invalid-refresh-token',
  AUTH_INVALID_GRANT_TYPE = 'auth/invalid-grant-type',
  AUTH_EMAIL_NOT_FOUND = 'auth/email-not-found',
  AUTH_CREDENTIAL_ALREADY_IN_USE = 'auth/credential-already-in-use',
  AUTH_ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL = 'auth/account-exists-with-different-credential',
  AUTH_REQUIRES_RECENT_LOGIN = 'auth/requires-recent-login',
  AUTH_PROVIDER_ALREADY_LINKED = 'auth/provider-already-linked',
  AUTH_NO_SUCH_PROVIDER = 'auth/no-such-provider',
  AUTH_INVALID_USER_TOKEN = 'auth/invalid-user-token',
  AUTH_INVALID_CUSTOM_TOKEN = 'auth/invalid-custom-token',
  AUTH_CUSTOM_TOKEN_MISMATCH = 'auth/custom-token-mismatch',
  AUTH_INVALID_CREDENTIAL = 'auth/invalid-credential',
  AUTH_USER_MISMATCH = 'auth/user-mismatch',
  // Auth errors
  AUTH_INVALID_CREDENTIALS = 'auth/invalid-credentials',
  AUTH_USER_DISABLED = 'auth/user-disabled',
  AUTH_TOO_MANY_REQUESTS = 'auth/too-many-requests',
  AUTH_WEAK_PASSWORD = 'auth/weak-password',
  AUTH_EMAIL_IN_USE = 'auth/email-already-in-use',
  AUTH_INVALID_EMAIL = 'auth/invalid-email',
  AUTH_SESSION_EXPIRED = 'auth/session-expired',
  
  // Passkey errors
  PASSKEY_NOT_SUPPORTED = 'passkey/not-supported',
  PASSKEY_REGISTRATION_FAILED = 'passkey/registration-failed',
  PASSKEY_VERIFICATION_FAILED = 'passkey/verification-failed',
  PASSKEY_CHALLENGE_NOT_FOUND = 'passkey/challenge-not-found',
  PASSKEY_CHALLENGE_EXPIRED = 'passkey/challenge-expired',
  PASSKEY_CREDENTIAL_NOT_FOUND = 'passkey/credential-not-found',
  PASSKEY_INVALID_CREDENTIAL = 'passkey/invalid-credential',
  PASSKEY_USER_CANCELLED = 'passkey/user-cancelled',

  // Data errors
  DATA_NOT_FOUND = 'data/not-found',
  DATA_PERMISSION_DENIED = 'data/permission-denied',
  DATA_INVALID_FORMAT = 'data/invalid-format',
  DATA_ALREADY_EXISTS = 'data/already-exists',
  DATA_CONFLICT = 'data/conflict',

  // Booking errors
  BOOKING_UNAVAILABLE_TIME = 'booking/unavailable-time',
  BOOKING_INVALID_SERVICE = 'booking/invalid-service',
  BOOKING_STAFF_UNAVAILABLE = 'booking/staff-unavailable',

  // Contact errors
  CONTACT_INVALID_DATA = 'contact/invalid-data',
  CONTACT_DUPLICATE = 'contact/duplicate',
  CONTACT_NOT_FOUND = 'CONTACT_NOT_FOUND',
  CONTACT_FETCH_FAILED = 'CONTACT_FETCH_FAILED',
  CONTACT_CREATION_FAILED = 'CONTACT_CREATION_FAILED',
  CONTACT_UPDATE_FAILED = 'CONTACT_UPDATE_FAILED',
  CONTACT_DELETION_FAILED = 'CONTACT_DELETION_FAILED',

  // Task errors
  TASK_INVALID_DATA = 'task/invalid-data',
  TASK_COMPLETION_FAILED = 'task/completion-failed',

  // Generic errors
  SERVER_ERROR = 'server/error',
  NETWORK_ERROR = 'network/error',
  UNKNOWN_ERROR = 'unknown/error',

  // Label errors
  DATA_FETCH_FAILED = 'Error fetching labels',
  LABEL_CREATION_FAILED = 'Error creating label',
  LABEL_UNASSIGN_FAILED = 'LABEL_UNASSIGN_FAILED',
  LABEL_DELETION_FAILED = 'LABEL_DELETION_FAILED',
  LABEL_UPDATE_FAILED = 'LABEL_UPDATE_FAILED',
  LABEL_ASSIGN_FAILED = 'LABEL_ASSIGN_FAILED',
  LABEL_ASSIGN_SINGLE_FAILED = 'LABEL_ASSIGN_SINGLE_FAILED',
  LABEL_COLOR_STORE_FAILED = 'LABEL_COLOR_STORE_FAILED',
  LABEL_COLOR_CLEANUP_FAILED = 'LABEL_COLOR_CLEANUP_FAILED',
  LABEL_INFO_FETCH_FAILED = 'LABEL_INFO_FETCH_FAILED',
  LABEL_CONTACT_COUNT_FAILED = 'LABEL_CONTACT_COUNT_FAILED',
  LABEL_CONTACT_COUNTS_FETCH_FAILED = 'LABEL_CONTACT_COUNTS_FETCH_FAILED',
}

/**
 * Standard API error interface for strong typing of errors from backend
 */
export interface ApiError {
  message: string;        // Human-readable error message
  code: ApiErrorCode;     // Error code for programmatic handling
  status?: number;        // HTTP status code if applicable
  details?: Record<string, unknown>;  // Additional error details if available
}

/**
 * Helper function to create standardized API errors
 */
export function createApiError(
  code: ApiErrorCode,
  message?: string,
  status?: number,
  details?: Record<string, unknown>
): ApiError {
  return {
    code,
    message: message || getDefaultMessageForCode(code),
    status,
    details
  };
}

/**
 * Get default error message for a given error code
 */
function getDefaultMessageForCode(code: ApiErrorCode): string {
  switch (code) {
    case ApiErrorCode.AUTH_INVALID_CREDENTIALS:
      return 'Invalid email or password';
    case ApiErrorCode.AUTH_USER_DISABLED:
      return 'This account has been disabled';
    case ApiErrorCode.AUTH_TOO_MANY_REQUESTS:
      return 'Too many login attempts. Please try again later';
    case ApiErrorCode.AUTH_WEAK_PASSWORD:
      return 'Password is too weak';
    case ApiErrorCode.AUTH_EMAIL_IN_USE:
      return 'Email is already in use';
    case ApiErrorCode.DATA_NOT_FOUND:
      return 'The requested resource was not found';
    case ApiErrorCode.DATA_PERMISSION_DENIED:
      return 'You do not have permission to access this resource';
    case ApiErrorCode.BOOKING_UNAVAILABLE_TIME:
      return 'The selected time is not available';
    case ApiErrorCode.CONTACT_INVALID_DATA:
      return 'Invalid contact information provided';
    case ApiErrorCode.TASK_COMPLETION_FAILED:
      return 'Failed to complete the task';
    case ApiErrorCode.PASSKEY_NOT_SUPPORTED:
      return 'Passkeys are not supported on this device';
    case ApiErrorCode.PASSKEY_REGISTRATION_FAILED:
      return 'Failed to register passkey';
    case ApiErrorCode.PASSKEY_VERIFICATION_FAILED:
      return 'Failed to verify passkey';
    case ApiErrorCode.PASSKEY_CHALLENGE_NOT_FOUND:
      return 'Authentication challenge not found or expired';
    case ApiErrorCode.PASSKEY_CHALLENGE_EXPIRED:
      return 'Authentication challenge has expired';
    case ApiErrorCode.PASSKEY_CREDENTIAL_NOT_FOUND:
      return 'Passkey not found for this account';
    case ApiErrorCode.PASSKEY_INVALID_CREDENTIAL:
      return 'Invalid passkey credential';
    case ApiErrorCode.PASSKEY_USER_CANCELLED:
      return 'Passkey authentication was cancelled';
    case ApiErrorCode.SERVER_ERROR:
      return 'Server error occurred';
    case ApiErrorCode.NETWORK_ERROR:
      return 'Network connection issue';
    default:
      return 'An unexpected error occurred';
  }
}